import { cls } from "cls";
import React from "react";
import { Store } from "@wss/store";
import Button from "~/common/Buttons";
import Icon from "~/common/Icons/Standard";
import PaginationModel from "~/common/Store/models/Pagination";
import CompareProductsToggle from "./components/CompareProductsToggle";
import OutletPoliciesButton from "../OutletPage/OutletPoliciesButton";
import SortByDropdown from "./SortByDropdown";
import ToggleView from "./ToggleView";
import { CompareProductsState } from "../compareProductStore";
import { ChromeBarState } from "./useChromeBarComponentStore";

interface Props {
  title?: string;
  hasHeaderData: boolean;
  displayFilters: boolean;
  displaySortByDropdown?: boolean;
  pageName: string;
  pageUrl: string;
  pagination: PaginationModel;
  displayTopper: boolean;
  displayOutletPoliciesButton?: boolean;
  chromeBarStore: Store<ChromeBarState>;
  compareProductsStore: Store<CompareProductsState>;
  toggleShowMobileFilter: () => void;
  toggleShowMobileSortFilter: () => void;
  compareProductsToggleRef?: React.RefObject<HTMLButtonElement>;
}

const Chrome = ({
  title = undefined,
  hasHeaderData,
  displayFilters,
  displaySortByDropdown = true,
  pageName,
  pageUrl,
  pagination,
  displayOutletPoliciesButton = false,
  chromeBarStore,
  compareProductsStore,
  toggleShowMobileFilter,
  toggleShowMobileSortFilter,
  compareProductsToggleRef,
}: Props) => {
  const handleCompareProductsActiveChanged = (active: boolean) =>
    compareProductsStore.setState({ active });

  const isSearchPage =
    pageName === "SearchPage" || pageName === "ImageSearchPage";

  return (
    <div
      className={cls(
        "bg-gray-50",
        "border-gray-200",
        "rounded-none",
        "border-solid",
        "border",
        "flex",
        "items-center",
        "clear-both",
        "-mx-2-1/2",
        "z-5",
        "xs:mx-0",
        "sm:rounded-t",
        "lt:-mb-px",
        hasHeaderData ? cls("-mt-2", "xs:-mt-8") : "",
        isSearchPage ? cls("mt-4", "xs:mt-4") : "",
        displayOutletPoliciesButton
          ? cls("flex-wrap", "[@media(min-width:415px)]:flex-nowrap")
          : "",
      )}
    >
      {displayFilters && (
        <div
          className={cls(
            "border-gray-200",
            "border-solid",
            "border-0",
            "border-r",
            "hidden",
            "flex-shrink-0",
            "lt:block",
            "place-self-stretch",
          )}
          id="filterToggle"
          style={{ width: 208 }}
        >
          <label
            className={cls(
              "font-semibold",
              "text-xs",
              "mb-0 mx-2",
              "flex h-full",
              "align-middle",
              "items-center",
            )}
          >
            Filters
          </label>
        </div>
      )}
      <div className={cls("flex", "my-1", "mx-4", "w-full", "sm:mx-2")}>
        <div className={cls("flex", "items-center", "m-0")}>
          <ToggleView chromeBarStore={chromeBarStore} />
          {displaySortByDropdown && (
            <SortByDropdown
              pageUrl={pageUrl}
              includeRelevancy={isSearchPage}
              includeMostPopular={!isSearchPage}
            />
          )}
        </div>
        <div className={cls("flex-1")}>
          {title && title !== "" && (
            <div className="ag-sort btn-group form-inline">
              <label
                className={cls(
                  "text-shadow-none",
                  "font-semibold",
                  "text-xs",
                  "m-0",
                  "mx-2",
                  "uppercase",
                  "align-middle",
                )}
              >
                {title}
              </label>
            </div>
          )}
        </div>
        {displayOutletPoliciesButton && (
          <div className="btn-toolbar pull-right my-0 mx-0 xxs:mx-1">
            <OutletPoliciesButton />
          </div>
        )}
      </div>
      <div
        className={cls(
          "flex",
          "my-1",
          "mx-1",
          "w-full",
          "sm:mx-2",
          displayOutletPoliciesButton
            ? "justify-start xxs:justify-end ml-4 lt:hidden"
            : "justify-end",
        )}
      >
        <div className="flex items-center pr-6 lt:pr-0">
          {pagination &&
            pagination.totalNumberOfItems > 1 &&
            !displayOutletPoliciesButton && (
              <CompareProductsToggle
                onChange={handleCompareProductsActiveChanged}
                ref={compareProductsToggleRef}
              />
            )}
        </div>
        {displaySortByDropdown && (
          <Button
            type="button"
            size="toolbar"
            color="ui"
            className="flex items-center bg-zinc-100 lt:hidden"
            onClick={() => toggleShowMobileSortFilter()}
            data-toggle="toolbarMenu"
            data-target="#mobileSort"
            data-testid="mobileSort"
            aria-haspopup="dialog"
          >
            <Icon width="15" height="10" name="sort" />
            <span className="ml-1">Sort</span>
          </Button>
        )}
        {displayFilters && (
          <Button
            type="button"
            size="toolbar"
            color="ui"
            className="flex items-center ml-1 bg-zinc-100 lt:hidden"
            data-target="#sidebar"
            data-original-title=""
            aria-haspopup="dialog"
            title=""
            onClick={() => {
              toggleShowMobileFilter();
            }}
          >
            <Icon width="11" height="9" name="filter" />
            <span className="ml-1">Filter</span>
          </Button>
        )}
      </div>
    </div>
  );
};

export default Chrome;
